/**
 * EL CONTRATO DE LAS REGLAS DEL POLICÍA.
 *
 * 🔴 Vive en `shared` y no en uno de los dos lados a propósito. Es la pieza que
 * hace que «todo lo que decide se controla desde la pantalla» sea una garantía y
 * no una promesa: la pantalla pinta esta lista, el backend la lee, y
 * `contrato-del-policia.test.js` falla si un campo declarado aquí no llega a
 * `leerConfig()` o no lo mira ninguna de las piezas que deciden.
 *
 * El problema que cierra: tres veces seguidas una regla vivió en una constante
 * del backend que nadie podía ver, y las tres veces paró la cola en silencio —
 * `hora_inicio_frio` (un campo que la pantalla guardaba y no leía nadie),
 * `PROTECTED_STATES` (duplicaba la lista configurable y ganaba la de código) y
 * `EXCLUDED_CLIENTS` (retenía 351 tareas sin que hubiera dónde mirarlo).
 *
 * Y el problema que cierra el test, que es el nuevo: al cablear 18 campos, si el
 * backend obedece 16, queda una pantalla con pinta de autoridad que no la tiene.
 * Eso sería peor que antes, porque antes al menos se desconfiaba de ella.
 */

/**
 * Los CUATRO supuestos: el eje real de la decisión.
 *
 * No es el tipo de tarea. Dos `new_whatsapp` pueden ser cosas opuestas — sobre
 * alguien a quien nunca escribimos es abordar a un desconocido; sobre alguien al
 * que ya mandamos dos, es insistir — y el riesgo no se parece en nada. El
 * 14/09/2026 se sellaron 4 primeros contactos sobre gente que ya había
 * contestado, justo por mezclarlos.
 *
 * Y la diferencia entre ellos no es el turno, es el cupo: «no es que entren
 * antes, es que no llenan ningún cupo; son libres porque nunca nos banearán por
 * escribir en un chat donde hay respuesta del deudor».
 */
const SUPUESTOS = Object.freeze([
  {
    id: 'respuesta',
    nombre: 'Respuesta',
    cuando: 'El deudor acaba de escribir y hay que contestarle.',
    riesgo: 'Ninguno: nadie banea un número por contestar a quien le escribe.',
  },
  {
    id: 'seguimiento',
    nombre: 'Seguimiento',
    cuando: 'El deudor contestó en algún momento, aunque haga semanas.',
    riesgo: 'Ninguno: hay conversación abierta, no es un desconocido.',
  },
  {
    id: 'repeticion',
    nombre: 'Repetición',
    cuando: 'Ya le escribimos y no ha contestado nunca.',
    riesgo: 'Alto: insistir sin respuesta es el patrón que quema un número.',
  },
  {
    id: 'primer_contacto',
    nombre: 'Primer contacto',
    cuando: 'Nunca le hemos escrito por WhatsApp.',
    riesgo: 'El más alto: es abordar a alguien que no te conoce de nada.',
  },
]);

/** Qué puede hacer el policía con un supuesto. */
const MODOS = Object.freeze([
  {
    id: 'directo',
    nombre: 'Directo a revisión',
    detalle: 'En cuanto vence la fecha. No mira cupo, ni hora de frío, ni estados.',
  },
  {
    id: 'con_reglas',
    nombre: 'Sigue las reglas',
    detalle: 'Espera cupo, hora de frío y estados. Puede quedarse retenido.',
  },
]);

/**
 * Los campos que la pantalla manda y el backend obedece.
 *
 * `donde` dice qué fichero del backend tiene que mirarlo: es lo que comprueba el
 * test. Si añades un campo aquí y nadie lo lee, el test lo dice con su nombre.
 */
const CAMPOS = Object.freeze([
  { campo: 'entrada', donde: 'dispatcher', que: 'Qué tareas mira y los topes de la pasada' },
  { campo: 'ventana', donde: 'dispatcher', que: 'Días y horas en que puede despachar' },
  { campo: 'supuestos', donde: 'dispatcher', que: 'Modo y orden de cada uno de los cuatro' },
  { campo: 'estados_urgentes', donde: 'dispatcher', via: 'esUrgente', que: 'Qué estados adelantan a todo' },
  { campo: 'orden_de_clientes', donde: 'dispatcher', via: 'ordenDeCliente', que: 'A qué cliente se atiende antes' },
  { campo: 'clientes_aparcados', donde: 'dispatcher', via: 'clienteAparcado', que: 'A quién no se le escribe, y con qué alcance' },
  { campo: 'estados_fuera_del_frio', donde: 'coldQueue', que: 'Estados sin primeros contactos' },
  { campo: 'bloquear_primer_contacto_si_ya_hablo', donde: 'coldQueue', que: 'Retener el «me presento» a quien ya respondió' },
  { campo: 'paises_permitidos', donde: 'routing', via: 'paisDe', que: 'A qué numeraciones se escribe' },
  { campo: 'orden_de_canales', donde: 'dispatcher', que: 'Por qué canal se prueba primero' },
  { campo: 'emparejar_pais_de_linea', donde: 'dispatcher', que: 'Que la línea sea del país del deudor' },
  { campo: 'idioma_por_cliente', donde: 'routing', via: 'idiomaPara', que: 'Clientes que escriben en otro idioma' },
  { campo: 'idioma_por_defecto', donde: 'routing', via: 'idiomaPara', que: 'El idioma de los demás' },
  { campo: 'canales', donde: 'rateLimiter', que: 'El cupo en frío de cada canal' },
]);

/**
 * Lo que NO se cambia, y por qué.
 *
 * 🔴 Se enseñan en la pantalla con la misma letra que las demás, pero sin
 * casilla. La distinción importa y costó llegar a ella: la queja de Edgar era
 * que estas reglas **no se veían**, no que no se pudieran cambiar, y son cosas
 * distintas. Un interruptor que nunca hay que mover no es un mando: es una
 * trampa esperando a las once de la noche.
 *
 * El criterio para estar aquí: apagarlo es SIEMPRE un error, en cualquier
 * escenario que se nos ocurra. Si hay un caso legítimo, va en `CAMPOS`.
 */
const INVARIANTES = Object.freeze([
  {
    id: 'solo_del_agente',
    titulo: 'Solo entran tareas del agente de IA',
    explica: 'Una tarea asignada a una persona no pasa por el policía.',
    porque: 'Es la definición del sistema. Si entraran las de personas, el agente escribiría por encima del trabajo de alguien.',
  },
  {
    id: 'respetar_linea_de_la_conversacion',
    titulo: 'El mensaje sale del número que ya habló',
    explica:
      'Si hubo conversación —la escribiéramos nosotros o la empezara él— se usa esa línea. Si su ficha murió, se busca otra ficha con el mismo número. Y si no queda ninguna, el mensaje sale por otra línea pero contando como PRIMER CONTACTO: descuenta cupo en frío y espera turno.',
    porque:
      'Desde otro número el deudor ve a un desconocido insistiéndole. Antes eso se retenía, y retener no continuaba la conversación: la congelaba para siempre, porque una línea muerta no vuelve. Ahora sale, pero pagando lo que cuesta.',
  },
  {
    id: 'saltar_expedientes_cerrados',
    titulo: 'Un expediente cerrado no recibe nada',
    explica: 'Se comprueba antes que ninguna otra regla, también en los supuestos que van directos.',
    porque: 'Escribir a alguien cuyo expediente ya está cerrado siempre es un error, y suele ser uno caro.',
  },
  {
    id: 'saltar_numeros_sin_whatsapp',
    titulo: 'Un número sin WhatsApp no se reintenta',
    explica: 'Cuando se comprueba que no tiene, deja de intentarse.',
    porque: 'Insistir abre el mismo chat inexistente una y otra vez desde la misma línea, que es de los patrones que peor pinta tienen.',
  },
  {
    id: 'un_mensaje_por_expediente',
    titulo: 'Un expediente recibe un mensaje cada vez, no dos',
    explica:
      'Si un expediente ya tiene una tarea de WhatsApp sellada y sin enviar, sus demás tareas esperan — da igual que sean de tipos distintos.',
    porque:
      'El sello iba por tarea y un expediente puede tener varias a la vez. El 14/09/2026 Jordi Martínez tenía una respuesta de julio y un seguimiento del 10/09: se sellaron con cinco minutos de diferencia, salió el primer mensaje y el segundo se quedó esperando aprobación para decirle lo mismo otra vez.',
  },
  {
    id: 'lo_comprometido_cuenta',
    titulo: 'Lo sellado y sin enviar gasta cupo igual que lo enviado',
    explica: 'Una tarea sellada ocupa sitio en el cupo de su línea aunque el mensaje no haya salido todavía.',
    porque: 'No es un ajuste, es contabilidad. Sin esto el policía reparte el mismo cupo una y otra vez: el 14/09/2026 selló 210 tareas sobre un cupo de 44.',
  },
]);

/** Solo los nombres, para recorrerlos. */
const NOMBRES_DE_CAMPO = Object.freeze(CAMPOS.map((c) => c.campo));

module.exports = { SUPUESTOS, MODOS, CAMPOS, NOMBRES_DE_CAMPO, INVARIANTES };
